import { MutableRefObject, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { SnackbarKey, useSnackbar } from "notistack";
// Material
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
// Constants
import actionsList from "@/Constants/actionsList";
// Components
import RowFeedback from "@Common/helpers/RowFeedback";
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";
import PayrollSubmitTable from "../../tables/PayrollClosing/PayrollSubmitTable";
import CommissionCyclePayrollSubmissionHistoryLoader from "./CommissionCyclePayrollSubmissionHistoryLoader";
import CommissionCyclePayrollSubmissionHistoryDialog from "./CommissionCyclePayrollSubmissionHistoryDialog";
// API
import { deleteSubmittedPayroll } from "@/Washouts/api/PayrollClosing/mutations";
import { useSubmissionHistoryData } from "@/Washouts/hooks/PayrollClossing/useSubmissionHistoryData";
// Types
import { IPayrollClosingSideBarSelected } from "@/Washouts/types/PayrollClosing/PayrollClosingSideBar";
import { usePayrollClosingSubscriptions } from "@/Washouts/hooks/PayrollClossing/usePayrollClosingSubscriptions";
import { ICommissionCycle, IPayPeriod } from "@/Common/api/types/CommissionCycle";

type CommissionCyclePayrollSubmissionHistoryProps = {
	locationId?: string;
	selectedItem: IPayrollClosingSideBarSelected | null;
	commissionCycle?: ICommissionCycle;
	payPeriod?: IPayPeriod;
	selection: boolean;
	current: boolean;
};

export default function CommissionCyclePayrollSubmissionHistory(props: Readonly<CommissionCyclePayrollSubmissionHistoryProps>) {
	const { locationId = "", selectedItem, selection, current, commissionCycle, payPeriod } = props;

	const [openDialog, setOpenDialog] = useState(false);
	const [previewId, setPreviewId] = useState("");

	const { enqueueSnackbar } = useSnackbar();

	const { useAuth } = useAuthorization();
	const submitPayroll = useAuth(actionsList.SUBMIT_PAYROLL);

	const { mutate: deleteSubmission, isPending: deleting } = useMutation({
		mutationFn: (ids: string[]) => deleteSubmittedPayroll(locationId, ids),
		onSuccess: () => RowFeedback?.skeleton(null, null)?.successDelete(true),
		onError: (error) => {
			RowFeedback?.skeleton(null, null)?.errorDelete(true);
			const message = error?.toString().replace(/Error:/gi, " ");
			const renderSnackbarsActions = (key: SnackbarKey) => {
				return <SnackbarsActions key={key.toString()} index={key} />;
			};
			enqueueSnackbar(<SnackbarsContent message={message} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: renderSnackbarsActions,
			});
		},
	});

	const { data: submittedData, isLoading } = useSubmissionHistoryData({
		locationId,
		commissionCycleId: selectedItem?.cycleId ?? "",
		payPeriodId: selectedItem?.payPeriodId ?? "",
	});

	const reviewSubmitted = (id: string) => {
		setOpenDialog(true);
		setPreviewId(id);
	};

	const deleteSubmitted = async (ids: GridRowSelectionModel, apiRef: MutableRefObject<GridApiPremium>) => {
		ids?.forEach((rowId) => {
			setTimeout(() => RowFeedback?.skeleton(rowId, apiRef)?.delete()?.display());
		});

		deleteSubmission(ids.map((id) => id.toString()));
	};

	usePayrollClosingSubscriptions({
		commissionCycleId: selectedItem?.cycleId ?? "",
		payPeriodId: selectedItem?.payPeriodId ?? "",
		providerPayPeriodId: "",
		// providerPayPeriodId: selectedItem?.providerPayPeriodId ?? "", TODO: providerPayPeriodId is not in the IPayrollClosingSideBarSelected
		locationId,
		current,
	});

	return (
		<>
			<CommissionCyclePayrollSubmissionHistoryLoader loading={isLoading} />
			{selection && !isLoading && (
				<Grid container>
					<Grid item xs={12}>
						<Typography className="page-title">{"Payroll Submission History"}</Typography>
					</Grid>
					<Grid item xs={12} sx={{ paddingTop: "20px" }}>
						<PayrollSubmitTable
							data={submittedData}
							showSubmitted={reviewSubmitted}
							allowDeletion={submitPayroll}
							disabled={deleting}
							deleteSubmitted={deleteSubmitted}
							selectedItem={selectedItem}
							commissionCycle={commissionCycle}
							payPeriod={payPeriod}
						/>
					</Grid>
					<CommissionCyclePayrollSubmissionHistoryDialog
						locationId={locationId}
						previewId={previewId}
						openDialog={openDialog}
						setOpenDialog={setOpenDialog}
					/>
				</Grid>
			)}
		</>
	);
}
